// Target of the e-mail link: /reset-password?token=… (or ?error=INVALID_TOKEN).
import { CircleAlert, KeyRound, Link2Off, LoaderCircle } from 'lucide-react';
import { useId, useState, type FormEvent } from 'react';
import { PasswordField } from '../components/PasswordField';
import {
  AuthError,
  PASSWORD_MAX_LENGTH,
  PASSWORD_MIN_LENGTH,
  RESET_LINK_MINUTES,
  authErrorMessage,
  resetPassword,
} from '../lib/authClient';

export function ResetPasswordView({ onDone, onRequestNewLink }: { onDone: () => void; onRequestNewLink: () => void }) {
  const params = new URLSearchParams(window.location.search);
  const token = params.get('token');
  const linkError = params.get('error');
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [error, setError] = useState<{ text: string; field: 'password' | 'repeat' | 'form' } | null>(null);
  const [state, setState] = useState<'form' | 'busy' | 'invalid-link'>(!token || linkError ? 'invalid-link' : 'form');
  const errorId = useId();

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (password.length < PASSWORD_MIN_LENGTH) {
      setError({ text: `Das Passwort muss mindestens ${PASSWORD_MIN_LENGTH} Zeichen lang sein.`, field: 'password' });
      return;
    }
    if (password.length > PASSWORD_MAX_LENGTH) {
      setError({ text: `Das Passwort darf höchstens ${PASSWORD_MAX_LENGTH} Zeichen lang sein.`, field: 'password' });
      return;
    }
    if (password !== repeat) {
      setError({ text: 'Die beiden Passwörter stimmen nicht überein.', field: 'repeat' });
      return;
    }
    if (!token) return;
    setError(null);
    setState('busy');
    try {
      await resetPassword(token, password);
      onDone();
    } catch (err) {
      if (err instanceof AuthError && err.code === 'invalid_token') {
        setState('invalid-link');
        return;
      }
      setState('form');
      const field = err instanceof AuthError && err.code.startsWith('password_') ? 'password' : 'form';
      setError({ text: authErrorMessage(err, 'reset'), field });
    }
  };

  if (state === 'invalid-link') {
    return (
      <main className="auth">
        <section className="auth__card glass glass--strong">
          <span className="auth__icon auth__icon--warn" aria-hidden="true">
            <Link2Off size={26} />
          </span>
          <h1 className="auth__title">{token || linkError ? 'Link abgelaufen' : 'Link unvollständig'}</h1>
          <p className="auth__sub">
            {token || linkError
              ? `Ein Link zum Festlegen des Passworts gilt nur ${RESET_LINK_MINUTES} Minuten und nur einmal. Fordere einfach einen neuen an.`
              : 'Diesem Link fehlt ein Teil. Öffne ihn direkt aus der E-Mail oder fordere einen neuen an.'}
          </p>
          <button type="button" className="btn btn--primary btn--block" onClick={onRequestNewLink}>
            Neuen Link anfordern
          </button>
        </section>
      </main>
    );
  }

  const busy = state === 'busy';
  return (
    <main className="auth">
      <section className="auth__card glass glass--strong">
        <span className="auth__icon" aria-hidden="true">
          <KeyRound size={26} />
        </span>
        <h1 className="auth__title">Neues Passwort</h1>
        <p className="auth__sub">Danach meldest du dich mit dem neuen Passwort an.</p>
        <form className="form" onSubmit={submit} noValidate>
          <PasswordField
            label="Neues Passwort"
            autoComplete="new-password"
            value={password}
            onChange={setPassword}
            hint={`Mindestens ${PASSWORD_MIN_LENGTH} Zeichen.`}
            invalid={error?.field === 'password' || error?.field === 'form'}
            describedBy={error ? errorId : undefined}
          />
          <PasswordField
            label="Passwort wiederholen"
            autoComplete="new-password"
            value={repeat}
            onChange={setRepeat}
            invalid={error?.field === 'repeat'}
            describedBy={error?.field === 'repeat' ? errorId : undefined}
          />
          {error && (
            <p id={errorId} className="inline-error" role="alert">
              <CircleAlert size={18} aria-hidden="true" />
              {error.text}
            </p>
          )}
          <button type="submit" className="btn btn--primary btn--block" disabled={busy}>
            {busy && <LoaderCircle size={18} className="spin" aria-hidden="true" />}
            {busy ? 'Wird gespeichert …' : 'Passwort speichern'}
          </button>
        </form>
      </section>
    </main>
  );
}
